import { Button } from '@whistler-ui/k2/components/Button';
import { Empty } from '@whistler-ui/k2/components/Empty';
import { Form } from '@whistler-ui/k2/components/Form';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import { USE_CASE_NAMES } from '@whistler-ui/k2/constants/websheetsConstants';
import { cleanEmptyFields } from '@whistler-ui/k2/helpers/objectUtils';
import { tryCatch } from '@whistler-ui/k2/helpers/servicesHelper';
import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';
import { useAppMessages } from '@whistler-ui/k2/hooks/useAppMessages';
import {
  useUpdateApiTestData,
  useUpdateUseCaseTestData,
} from '@whistler-ui/portal/hooks/queries/useUseCasesV2';
import { useEntitlementsStore } from '@whistler-ui/portal/store/entitlements';
import {
  TUseCasesApiTags,
  TUseCaseTags,
} from '@whistler-ui/portal/types/tests';
import { TUseCaseType } from '@whistler-ui/portal/types/useCases';
import { useFormik } from 'formik';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { TestDataPaneLoading } from './components/TestDataPaneLoading';
import DynamicDataFields from './DynamicDataFields';
import { getValidationSchema, setPayloadData } from './helpers';
import { useGetTestData } from './hooks/useGetTestData';

export interface Props {
  tag: TUseCaseTags | TUseCasesApiTags;
  test: any;
  type: TUseCaseType;
  isLoading: boolean;
}

export const TestDataPaneForm = memo(
  ({ tag, test, type, isLoading }: Props) => {
    const { t } = useTranslation();
    const [setMessages] = useAppMessages(t);
    const { current: environment } = useEntitlementsStore();
    const environmentId = environment?.id;
    const hasQueryParams = test?.query_parameters?.length > 0;

    const {
      isPending: testDataIsPending,
      formikData,
      parameters,
      isApiType,
      isUseCaseType,
    } = useGetTestData({
      tag,
      environmentId,
      test,
      type,
      hasQueryParams,
    });
    const { initialValues, rules } = formikData;
    const { mutateAsync: updateUseCaseTestCase } = useUpdateUseCaseTestData();
    const { mutateAsync: updateApiTestData } = useUpdateApiTestData();

    const formik = useFormik({
      enableReinitialize: true,
      initialValues,
      onSubmit: async (values, { setSubmitting }) => {
        // Helper function that parse data values payload.
        const { result: data, missingFields } = setPayloadData({ values, t });

        if (missingFields.length > 0) {
          setSubmitting(false);
          setMessages(missingFields);
          return;
        }

        const { error } = await tryCatch(async () => {
          const basePayload = {
            data: cleanEmptyFields(data),
            test_case_tag: `${test?.tag}`,
            params_model_version: '1.0.0' as const,
          };

          if (hasQueryParams) {
            basePayload.data = {
              ...test.parameters.reduce((map, { name }) => {
                map[name] = values[name];
                return map;
              }, {}),
              query_params: cleanEmptyFields({
                ...test.query_parameters.reduce((map, { name }) => {
                  // Map entitlements array into a string
                  map[name] =
                    name === 'entitlements' && Array.isArray(values[name])
                      ? values[name].join('|')
                      : values[name];
                  return map;
                }, {}),
              }),
            };
          }

          if (isUseCaseType) {
            await updateUseCaseTestCase({
              envId: environmentId,
              payload: {
                use_case_tag: tag as TUseCaseTags,
                ...basePayload,
              },
            });
          }

          if (isApiType) {
            await updateApiTestData({
              envId: environmentId,
              payload: {
                api_tag: tag as TUseCasesApiTags,
                ...basePayload,
              },
            });
          }
        });

        if (error) {
          setMessages({
            type: useAppMessages.type.ERROR,
            msg: getUserFacingErrorMessage(error, 'common:errors.default'),
          });
          return;
        }

        setMessages({
          type: useAppMessages.type.SUCCESS,
          msg: t('componentTestDataPane_save_success_msg'),
        });
      },
      validationSchema: yup.object().shape(getValidationSchema(rules)),
    });

    const { dirty, isValid, isSubmitting, handleSubmit } = formik;

    const renderContent = () => {
      // Conditional rendering for Health Check use case
      if (tag === USE_CASE_NAMES.HEALTH_CHECK) {
        return (
          <div className="my-10">
            <Empty.State>
              <Paragraph isBolder align="center">
                {t('componentTestDataPane_empty_state_no_test_data_required')}
              </Paragraph>
            </Empty.State>
          </div>
        );
      }

      return (
        <div className="flex py-0 px-6">
          <Form
            onSubmit={handleSubmit}
            className="p-0 m-0 border-0"
            formClassName="w-full max-w-[calc(var(--component-form-width-base)*2+2rem)]"
            loading={isSubmitting}
          >
            <Stack direction="column" className="gap-[18px]!">
              <Stack direction="column" gap={2}>
                <Stack direction="row" align="center" className="gap-[0.3rem]!">
                  <span className="after:content-['_*_'] after:text-red-500 after:text-sm after:font-bold" />
                  <Paragraph size="xs">
                    {t('componentTestDataPane_required_field')}
                  </Paragraph>
                </Stack>
                <DynamicDataFields formik={formik} parameters={parameters} />
              </Stack>
              <footer>
                <Button
                  type="submit"
                  id={`submit-${test.tag}`}
                  disabled={!isValid || !dirty || isSubmitting}
                  loading={isSubmitting}
                  data-cy="test-data-pane-save-button"
                >
                  {t('componentTestDataPane_form_button_save_details')}
                </Button>
              </footer>
            </Stack>
          </Form>
        </div>
      );
    };

    if (isLoading || testDataIsPending) {
      return <TestDataPaneLoading />;
    }

    return (
      <Stack direction="column" gap={4}>
        <div className="bg-(--bg-tertiary) py-2 px-6 m-0">
          <Paragraph>{`${test.title} - ${test.tag_line}`}</Paragraph>
        </div>

        {renderContent()}
      </Stack>
    );
  },
);

TestDataPaneForm.displayName = 'TestDataPaneForm';
